import React from 'react';
import { RefreshCw, AlertTriangle, MailQuestion } from 'lucide-react';

/**
 * AppRecovery — universal "we're reconnecting your family access" screen.
 *
 * STABILITY CHARTER:  see STABILITY.md §7 (Production Stability) and §8
 *                      (Invite + Account Recovery + Family Reconnection).
 * LEGACY DATA POLICY: see LEGACY_DATA_POLICY.md — this surface NEVER
 *                      mutates family graph data. It only renders.
 *
 * Shown by:
 *   1. <AppBootErrorBoundary> when ANY render error escapes from
 *      AppLayout (bad mapping, null viewerMemberId, missing parent_ids).
 *   2. <RouteErrorBoundary> when a route subtree crashes.
 *   3. <LayoutInner> in AppLayout.tsx when the bootstrap pipeline
 *      (auth → profile → family tree) takes longer than 8s.
 *
 * Pure UI. Never crashes. Never reads from a context that might not be
 * mounted. Never blocks any underlying tree state — it just renders on
 * top, and disappears the moment the user taps "Retry connection".
 *
 * IMPORTANT: must NOT import useFamily / useAuth / useMembership directly
 * because this component must render successfully even when one of those
 * providers has crashed.
 */
const AppRecovery: React.FC<{
  /** Optional internal-only diagnostic message (shown small, dismissible). */
  diagnostic?: string | null;
  /** Hard-reload the page to retry the full bootstrap pipeline. */
  onRetry?: () => void;
  /** Send the user back to the public invite/join landing page. */
  onReturnToInvite?: () => void;
  /**
   * Optional: trigger the password-reset / magic-link recovery email
   * flow. When omitted, the "Send recovery email" button is hidden so
   * the surface stays minimal for purely-render-error cases. When the
   * caller provides a handler, the button appears as a third action.
   * Per STABILITY.md §8.2, this surface NEVER sends an email itself —
   * it just dispatches to the existing AuthContext.sendPasswordReset
   * pipeline (Resend → Supabase fallback).
   */
  onSendRecoveryEmail?: () => void;
}> = ({ diagnostic, onRetry, onReturnToInvite, onSendRecoveryEmail }) => {
  const handleRetry = React.useCallback(() => {
    if (onRetry) {
      try {
        onRetry();
        return;
      } catch {
        /* fall through to hard reload */
      }
    }
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  }, [onRetry]);

  const handleReturnToInvite = React.useCallback(() => {
    if (onReturnToInvite) {
      try {
        onReturnToInvite();
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      window.location.assign('/join');
    } catch {
      /* ignore */
    }
  }, [onReturnToInvite]);

  const handleSendRecoveryEmail = React.useCallback(() => {
    if (!onSendRecoveryEmail) return;
    try {
      onSendRecoveryEmail();
    } catch {
      /* swallow — caller is responsible for surfacing toast feedback */
    }
  }, [onSendRecoveryEmail]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-fuchsia-50 to-purple-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-fuchsia-100 overflow-hidden">
        <div className="bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 text-white p-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <img src="/brand/kinscircle-tree-icon.png" alt="KinsCircle" className="w-12 h-12 rounded-xl object-contain" />
          </div>
          <div>
            <div className="font-serif text-xl font-bold leading-tight">
              Kins<span className="text-amber-100">Circle</span>
            </div>
            <div className="text-xs opacity-90">Reconnecting your family access</div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-700 leading-relaxed">
              We&apos;re reconnecting your family access. Please try again
              in a moment.
            </p>
          </div>
          <p className="text-xs text-slate-500">
            This usually means your invite, profile, or family membership
            mapping is still being set up. Your data is safe — nothing has
            been lost. The next step almost always works.
          </p>

          <div className="grid grid-cols-1 gap-2 pt-2">
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 text-white font-semibold py-3 text-sm shadow hover:opacity-95"
            >
              <RefreshCw className="w-4 h-4" />
              Retry connection
            </button>
            {onSendRecoveryEmail ? (
              <button
                type="button"
                onClick={handleSendRecoveryEmail}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-fuchsia-200 text-fuchsia-700 font-semibold py-3 text-sm hover:bg-fuchsia-50"
              >
                <MailQuestion className="w-4 h-4" />
                Send recovery email
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleReturnToInvite}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-fuchsia-200 text-fuchsia-700 font-semibold py-3 text-sm hover:bg-fuchsia-50"
            >
              <img src="/brand/kinscircle-tree-icon.png" alt="" className="w-4 h-4 rounded object-contain" />
              Return to invite / join family
            </button>
          </div>

          {diagnostic ? (
            <details className="pt-2">
              <summary className="text-[11px] text-slate-400 cursor-pointer select-none">
                Technical details
              </summary>
              <pre className="mt-2 text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 break-words whitespace-pre-wrap">
                {diagnostic}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AppRecovery;
